import { useAbility } from "@casl/react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ContactRound, FolderPlus, Plus, UserPlus } from "lucide-react";
import { type FC, useState } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { Button } from "@recobra/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@recobra/ui/dropdown-menu";

import { TaskCreationModal } from "../tasks";
import { CaseTaskMenuItems } from "./CaseTaskMenuItems";
import { useCaseTaskTypes } from "./useCaseTaskTypes";

export const CreationActions: FC = () => {
  const ability = useAbility<AppAbility>();
  const navigate = useNavigate();
  const { caseNumber: caseNumberParam, id: clientId } = useParams({ strict: false });
  const caseNumber = Number(caseNumberParam);
  const canCreateCaseTask = ability.can("create", "Task") && Number.isInteger(caseNumber);
  const canCreatePrimaryTask = ability.can("createPrimary", "Task");
  const canCreateCase = ability.can("create", "Case");
  const canCreateClient = ability.can("manage", "Client");
  const canCreateUser = ability.can("manage", "User");
  const [newTask, setNewTask] = useState<{ taskType?: string } | null>(null);
  const { kase, taskTypes, isFinalized } = useCaseTaskTypes(
    caseNumber,
    canCreateCaseTask,
    canCreatePrimaryTask,
  );
  const canCreateTask = taskTypes.length > 0;

  if (![canCreateTask, canCreateCase, canCreateClient, canCreateUser].some(Boolean)) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button type="button" size="icon-sm" aria-label={m.creation_actions_aria()} />}
        >
          <Plus />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuGroup>
            {canCreateTask ? (
              <CaseTaskMenuItems
                taskTypes={taskTypes}
                canCreatePrimary={canCreatePrimaryTask}
                disabled={isFinalized}
                onSelect={(taskType) => setNewTask({ taskType })}
              />
            ) : null}
            {canCreateCase ? (
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() =>
                  void navigate({ to: "/cases/new", search: clientId ? { clientId } : {} })
                }
              >
                <FolderPlus />
                {m.cases_new()}
              </DropdownMenuItem>
            ) : null}
            {canCreateClient ? (
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => void navigate({ to: "/clients/new" })}
              >
                <ContactRound />
                {m.clients_add()}
              </DropdownMenuItem>
            ) : null}
            {canCreateUser ? (
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => void navigate({ to: "/users/new" })}
              >
                <UserPlus />
                {m.users_add_user()}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {kase ? (
        <TaskCreationModal
          isOpen={!!newTask}
          onOpenChange={() => setNewTask(null)}
          context={{ contextType: "case", contextId: kase.id, contextStage: kase.step }}
          initialTaskType={newTask?.taskType}
        />
      ) : null}
    </>
  );
};
